/**
 * Legacy Breakpoints Adapter
 *
 * Bridges the deprecated vue-mq plugin with the new mediaQueriesCore engine.
 * This adapter provides the same reactive breakpoint properties (`$mq`, layout flags)
 * that Options API components expect from the old vue-mq plugin, without requiring
 * any changes to those components.
 *
 * Used by the legacy plugin installation to inject `mdLayout`, `lgLayout`, `xxlLayout`,
 * etc. as global properties available via `this.$mq` or direct mixin injection.
 *
 * New Composition API components should use `useMediaQueries` instead.
 *
 * @module legacyBreakpointsAdapter
 */
import { computed, type Ref, ref } from 'vue';
import {
    getLegacyBreakpointFromWidth,
    getSnapshot,
    onBreakpointChange,
    startResizeTracking,
    stopResizeTracking,
    type Breakpoint,
} from '@/utils/mediaQueriesCore.ts';

/**
 * Pure computation of legacy layout flags from reactive width/height refs.
 *
 * Derives layout breakpoint flags using the legacy vue-mq max-width logic.
 * Can be reused by both the legacy adapter plugin and the useMediaQueries composable.
 */
export function useLegacyLayoutBreakpoints(width: Ref<number>, height: Ref<number>) {
    const isLandscape = computed(() => width.value > height.value);

    const legacyBreakpoint = computed(() => getLegacyBreakpointFromWidth(width.value));

    const xsLayout = computed(() => ['xs'].includes(legacyBreakpoint.value));
    const smLayout = computed(() => ['xs', 'sm'].includes(legacyBreakpoint.value));
    const mdLayout = computed(() => ['xs', 'sm', 'md'].includes(legacyBreakpoint.value));
    const lgLayout = computed(() => ['xs', 'sm', 'md', 'lg'].includes(legacyBreakpoint.value));
    const xlLayout = computed(() =>
        ['xs', 'sm', 'md', 'lg', 'xl'].includes(legacyBreakpoint.value)
    );
    const oneXlLayout = computed(() =>
        ['xs', 'sm', 'md', 'lg', 'xl', '1xl'].includes(legacyBreakpoint.value)
    );
    const xxlLayout = computed(() => ['2xl', '3xl'].includes(legacyBreakpoint.value));
    const phoneLayout = computed(() => mdLayout.value || (lgLayout.value && isLandscape.value));

    return {
        isLandscape,
        legacyBreakpoint,
        xsLayout,
        smLayout,
        mdLayout,
        lgLayout,
        xlLayout,
        oneXlLayout,
        xxlLayout,
        phoneLayout,
    };
}

/**
 * Plugin-level adapter that wires up resize tracking and exposes legacy layout flags
 * as reactive properties for the Vue plugin system.
 */
export function createLegacyBreakpointsAdapter() {
    const snapshot = getSnapshot();
    const currentBreakpoint = ref<Breakpoint>(getLegacyBreakpointFromWidth(snapshot.width));
    const width = ref(snapshot.width);
    const height = ref(snapshot.height);

    const {
        isLandscape,
        xsLayout,
        smLayout,
        mdLayout,
        lgLayout,
        xlLayout,
        oneXlLayout,
        xxlLayout,
        phoneLayout,
    } = useLegacyLayoutBreakpoints(width, height);

    const unsubscribe = onBreakpointChange((nextSnapshot) => {
        currentBreakpoint.value = getLegacyBreakpointFromWidth(nextSnapshot.width);
        width.value = nextSnapshot.width;
        height.value = nextSnapshot.height;
    });

    startResizeTracking();

    return {
        currentBreakpoint,
        isLandscape,
        xsLayout,
        smLayout,
        mdLayout,
        lgLayout,
        xlLayout,
        oneXlLayout,
        xxlLayout,
        phoneLayout,
        stop() {
            unsubscribe();
            stopResizeTracking();
        },
    };
}
